/**
 * AuditTrailView — the firm's compliance audit trail (Phase 4/5).
 * Read-only; gated on firm.audit_view. Shows who did what, when.
 */
import React from 'react';

import { Heading } from '../../ui/heading';
import { Badge } from '../../ui/badge';
import { Timestamp } from '../../ui/timestamp';
import { Card, CardContent } from '../../ui/card';
import { useAccess, Capability } from '../../../hooks/useAccess';
import { useAuditEvents } from '../../../hooks/useAuditEvents';

const AuditTrailView: React.FC = () => {
  const { can } = useAccess();
  const canView = can(Capability.FirmAuditView);
  const { data: events, isLoading } = useAuditEvents();

  if (!canView) {
    return (
      <div className="p-6">
        <Heading level={2}>Audit trail</Heading>
        <p className="mt-2 text-sm text-muted-foreground">
          You need audit access for your organization to view this.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Heading level={2}>Audit trail</Heading>
        <p className="mt-1 text-sm text-muted-foreground">
          Every state-changing action in your organization, newest first.
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading audit trail…</p>
      )}
      {events && events.length === 0 && (
        <p className="text-sm text-muted-foreground">No audit events yet.</p>
      )}

      <div className="space-y-1">
        {events?.map((e) => (
          <Card key={e.id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Badge variant="secondary">{e.action}</Badge>
                <span className="text-sm text-muted-foreground">
                  {e.resourceType}
                  {e.resourceName
                    ? ` · ${e.resourceName}`
                    : e.resourceId
                      ? ` · ${e.resourceId.slice(0, 8)}`
                      : ''}
                </span>
                {e.actorName || e.actorUserId ? (
                  <span className="text-sm">
                    by {e.actorName ?? e.actorUserId!.slice(0, 8)}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">system</span>
                )}
              </div>
              <Timestamp value={e.createdAt} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AuditTrailView;
